import { useState, useEffect, useCallback } from "react";
import { X, Mail, Gift, Loader2, CheckCircle, Sparkles } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Checkbox } from "./ui/checkbox";
import { API } from "../App";

const POPUP_STORAGE_KEY = "happyages_discount_popup_dismissed";
const POPUP_DISMISS_DAYS = 30;
const TERMS_URL = "/terms"; // Update this when T&C page is ready

export default function DiscountPopup() {
  const [isVisible, setIsVisible] = useState(false);
  const [email, setEmail] = useState("");
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [discountCode, setDiscountCode] = useState("");
  const [error, setError] = useState("");
  const [hasTriggered, setHasTriggered] = useState(false);

  // Check if popup was recently dismissed
  const wasRecentlyDismissed = useCallback(() => {
    const dismissedAt = localStorage.getItem(POPUP_STORAGE_KEY);
    if (!dismissedAt) return false;
    
    const dismissedDate = new Date(parseInt(dismissedAt));
    const daysSince = (Date.now() - dismissedDate.getTime()) / (1000 * 60 * 60 * 24);
    return daysSince < POPUP_DISMISS_DAYS;
  }, []);

  // Check if user is already logged in
  const isUserLoggedIn = useCallback(() => {
    // Check for session cookie or localStorage flag
    return document.cookie.includes("session") || localStorage.getItem("user_logged_in");
  }, []);

  // Show popup
  const showPopup = useCallback(() => {
    if (!hasTriggered && !wasRecentlyDismissed() && !isUserLoggedIn()) {
      setIsVisible(true);
      setHasTriggered(true);
    }
  }, [hasTriggered, wasRecentlyDismissed, isUserLoggedIn]);

  // Handle exit intent
  useEffect(() => {
    const handleMouseLeave = (e) => {
      if (e.clientY <= 0) {
        showPopup();
      }
    };

    document.addEventListener("mouseleave", handleMouseLeave);
    return () => document.removeEventListener("mouseleave", handleMouseLeave);
  }, [showPopup]);

  // Timer fallback - show after 10 seconds
  useEffect(() => {
    const timer = setTimeout(() => {
      showPopup();
    }, 10000);

    return () => clearTimeout(timer);
  }, [showPopup]);

  // Handle dismiss
  const handleDismiss = () => {
    setIsVisible(false);
    localStorage.setItem(POPUP_STORAGE_KEY, Date.now().toString());
  };

  // Handle form submit
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setIsLoading(true);

    try {
      const response = await fetch(`${API}/promotions/subscribe`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: email.trim() })
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setIsSuccess(true);
        setDiscountCode(data.discount_code);
        // Mark as dismissed so it doesn't show again
        localStorage.setItem(POPUP_STORAGE_KEY, Date.now().toString());
      } else if (data.discount_code) {
        // Already subscribed - show their existing code
        setIsSuccess(true);
        setDiscountCode(data.discount_code);
        setError("You've already subscribed! Here's your code:");
      } else {
        setError(data.message || data.detail || "Something went wrong. Please try again.");
      }
    } catch (err) {
      console.error("Subscription error:", err);
      setError("Network error. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  if (!isVisible) return null;

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 animate-fade-in">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/70 backdrop-blur-sm"
        onClick={handleDismiss}
      />
      
      {/* Modal */}
      <div className="relative w-full max-w-md bg-gradient-to-br from-[#1E293B] to-[#0F172A] border border-[#D4AF37]/30 rounded-2xl shadow-2xl overflow-hidden animate-scale-in">
        {/* Close button */}
        <button
          onClick={handleDismiss}
          className="absolute top-4 right-4 text-[#94A3B8] hover:text-white transition-colors z-10"
          aria-label="Close"
        >
          <X className="w-5 h-5" />
        </button>
        
        {/* Decorative top accent */}
        <div className="h-1 bg-gradient-to-r from-[#D4AF37] via-[#F5E6B8] to-[#D4AF37]" />
        
        {!isSuccess ? (
          /* Sign up form */
          <div className="p-8">
            {/* Icon */}
            <div className="flex justify-center mb-6">
              <div className="relative">
                <div className="w-20 h-20 rounded-full bg-[#D4AF37]/10 flex items-center justify-center">
                  <Gift className="w-10 h-10 text-[#D4AF37]" />
                </div>
                <Sparkles className="w-6 h-6 text-[#D4AF37] absolute -top-1 -right-1 animate-pulse" />
              </div>
            </div>
            
            {/* Header */}
            <h2 className="font-cinzel text-2xl text-center text-[#F8FAFC] mb-2">
              EXCLUSIVE <span className="text-[#D4AF37]">OFFER</span>
            </h2>
            <p className="text-center text-[#94A3B8] mb-6 font-manrope">
              Join our Happy family and receive
            </p>
            
            {/* Discount badge */}
            <div className="bg-[#D4AF37]/10 border border-[#D4AF37]/30 rounded-xl p-4 mb-6 text-center">
              <span className="text-4xl font-bold text-[#D4AF37]">10% OFF</span>
              <p className="text-sm text-[#94A3B8] mt-1">Your first order</p>
            </div>
            
            {/* Form */}
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#64748B]" />
                <Input
                  type="email"
                  placeholder="Enter your email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="pl-10 bg-[#0F172A] border-[#334155] text-[#F8FAFC] placeholder:text-[#64748B] focus:border-[#D4AF37] focus:ring-[#D4AF37]/20"
                  data-testid="discount-email-input"
                />
              </div>
              
              {/* Terms & Conditions Checkbox */}
              <div className="flex items-start gap-3">
                <Checkbox
                  id="terms"
                  checked={agreedToTerms}
                  onCheckedChange={setAgreedToTerms}
                  className="mt-0.5 border-[#475569] data-[state=checked]:bg-[#D4AF37] data-[state=checked]:border-[#D4AF37]"
                  data-testid="discount-terms-checkbox"
                />
                <label 
                  htmlFor="terms" 
                  className="text-sm text-[#94A3B8] cursor-pointer leading-tight"
                >
                  I agree to the{" "}
                  <a 
                    href={TERMS_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-[#D4AF37] hover:underline"
                    onClick={(e) => e.stopPropagation()}
                  >
                    Terms & Conditions
                  </a>
                </label>
              </div>
              
              {error && (
                <p className="text-sm text-red-400 text-center">{error}</p>
              )}
              
              <Button
                type="submit"
                disabled={isLoading || !email || !agreedToTerms}
                className="w-full bg-gradient-to-r from-[#D4AF37] to-[#B5952F] text-[#020617] font-semibold hover:from-[#E5C349] hover:to-[#D4AF37] disabled:opacity-50 transition-all"
                data-testid="discount-submit-btn"
              >
                {isLoading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Sending...
                  </>
                ) : (
                  "GET MY 10% OFF"
                )}
              </Button>
            </form>
            
            <p className="text-xs text-[#64748B] text-center mt-4">
              No spam, ever. Unsubscribe anytime.
            </p>
          </div>
        ) : (
          /* Success state */
          <div className="p-8 text-center">
            <div className="flex justify-center mb-6">
              <div className="w-20 h-20 rounded-full bg-green-500/10 flex items-center justify-center">
                <CheckCircle className="w-10 h-10 text-green-500" />
              </div>
            </div>
            
            <h2 className="font-cinzel text-2xl text-[#F8FAFC] mb-2">
              YOU&apos;RE <span className="text-[#D4AF37]">IN!</span>
            </h2>
            <p className="text-[#94A3B8] mb-6 font-manrope">
              {error || "Check your email for your discount code!"}
            </p>
            
            {/* Discount code display */}
            <div className="bg-[#0F172A] border-2 border-dashed border-[#D4AF37] rounded-xl p-6 mb-6">
              <p className="text-sm text-[#94A3B8] mb-2">Your exclusive code:</p>
              <p className="text-2xl font-bold text-[#D4AF37] tracking-wider font-mono" data-testid="discount-code-display">
                {discountCode}
              </p>
              <p className="text-xs text-[#64748B] mt-2">Valid for 30 days • Single use</p>
            </div>
            
            <Button
              onClick={handleDismiss}
              className="w-full bg-[#334155] text-[#F8FAFC] hover:bg-[#475569]"
            >
              Start Shopping
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
